import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Field,
  Textarea,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { uploadTemplateImage } from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';

const DEFAULT_PRIMARY_COLOR = '#059669';
const DEFAULT_SECONDARY_COLOR = '#34d399';

const useStyles = makeStyles({
  uploadButton: {
    cursor: 'pointer',
    display: 'inline-block',
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase200,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  logoPreview: {
    height: '64px',
    width: '64px',
    borderRadius: tokens.borderRadiusMedium,
    objectFit: 'cover',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  colorSwatch: {
    height: '40px',
    width: '64px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
  },
});

export default function CreateWorkspaceModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const styles = useStyles();
  const { createWorkspace } = useWorkspace();
  const { currentUser } = useAuth();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY_COLOR);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError(t('workspaces.brandNameRequired'));
      return;
    }

    try {
      setLoading(true);

      let logoUrl = null;
      if (logo) {
        logoUrl = await uploadTemplateImage(logo, currentUser.uid, 'logos');
      }

      await createWorkspace({
        name: name.trim(),
        description: description.trim(),
        logoUrl,
        colors: {
          primary: primaryColor,
          secondary: secondaryColor,
        },
      });

      setName('');
      setDescription('');
      setLogo(null);
      setLogoPreview(null);
      setPrimaryColor(DEFAULT_PRIMARY_COLOR);
      setSecondaryColor(DEFAULT_SECONDARY_COLOR);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error creating workspace:', err);
      setError(t('workspaces.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <form onSubmit={handleSubmit}>
          <DialogBody>
            <DialogTitle>{t('workspaces.createBrand')}</DialogTitle>
            <DialogContent>
              <div className="space-y-4">
                <Field label={`${t('workspaces.brandName')} *`}>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('workspaces.namePlaceholder')}
                    required
                  />
                </Field>

                <Field label={t('workspaces.description')}>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('workspaces.descriptionPlaceholder')}
                    rows={2}
                  />
                </Field>

                <Field label={t('workspaces.brandLogo')}>
                  <div className="flex items-center gap-4">
                    {logoPreview && (
                      <img src={logoPreview} alt={t('workspaces.logoPreview')} className={styles.logoPreview} />
                    )}
                    <label className={styles.uploadButton}>
                      <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                      {logoPreview ? t('workspaces.changeLogo') : t('workspaces.uploadLogo')}
                    </label>
                  </div>
                </Field>

                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('workspaces.primaryColor')}>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className={styles.colorSwatch}
                      />
                      <Input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="flex-1" />
                    </div>
                  </Field>
                  <Field label={t('workspaces.secondaryColor')}>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className={styles.colorSwatch}
                      />
                      <Input type="text" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="flex-1" />
                    </div>
                  </Field>
                </div>

                {error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button type="button" onClick={onClose} variant="outline" disabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? t('workspaces.creating') : t('workspaces.createBrand')}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}
